import { useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api_authUserLogin } from "../axios";
import Logo from "../images/logo.svg";
import { recordReturnPath } from "../authNavigation";
import { clearLogoutNotice, getLogoutStatus, requestServerLogout, subscribeLogout } from "../logoutSession";

const Login = () => {
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [help, setHelp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const logoutStatus = useSyncExternalStore(subscribeLogout, getLogoutStatus);
  const logoutIncomplete = logoutStatus === "pending" || logoutStatus === "error";

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current || ["pending", "error"].includes(getLogoutStatus())) return;
    inFlight.current = true;
    setSubmitting(true);
    setError("");
    try {
      const result = await api_authUserLogin({ account, password });
      if (result?.status === "error" || !result?.data?.auth) {
        setError("帳號或密碼不正確，請確認後再試一次。");
      } else {
        localStorage.setItem("stats-insight-login", "true");
        localStorage.setItem("stats-insight-login-info", JSON.stringify({ id: result.data.sid, expiredTime: new Date(Date.now() + 6 * 3600 * 1000) }));
        clearLogoutNotice();
        navigate(recordReturnPath(location.state?.returnTo), { replace: true });
      }
    } catch {
      setError("目前無法登入，請確認網路連線後再試一次。");
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <main className="login-page">
      <div className="login-shell">
        <section className="login-story" aria-label="StatsInsight 影片記錄工作台">
          <img src={Logo} className="login-brand" alt="StatsInsight" />
          <div className="login-story-main">
            <span className="eyebrow">BASEBALL VIDEO WORKSPACE</span>
            <h1>每一球，<br />都有值得記錄的細節。</h1>
            <p>連結比賽影片，配對記錄軟體，<br />把場上的瞬間轉成精準的棒球數據。</p>
          </div>
          <ol className="login-steps"><li><b>01</b> 連結影片</li><li><b>02</b> 配對軟體</li><li><b>03</b> 標記球位</li></ol>
          <span className="login-signature">灼見運動數據 · StatsInsight</span>
        </section>
        <section className="login-panel" aria-labelledby="login-title">
          <span className="eyebrow">歡迎回到工作台</span>
          <h2 id="login-title">登入，開始記錄</h2>
          <p className="login-subtitle">使用你的 StatsInsight 帳號登入。</p>
          {logoutStatus === "pending" && <p role="status">本機已登出，正在清除伺服器登入狀態…</p>}
          {logoutStatus === "success" && <p role="status">已登出</p>}
          {logoutStatus === "error" && <div className="login-help" role="alert">本機已登出，但伺服器登入狀態尚未清除。請重試後再登入。<button type="button" onClick={requestServerLogout}>重試登出</button></div>}
          <form onSubmit={handleLoginSubmit} className="login-form" aria-label="登入" aria-busy={submitting}>
            <label htmlFor="login-account">帳號</label>
            <input id="login-account" name="username" autoComplete="username" placeholder="輸入帳號" value={account} onChange={(e) => setAccount(e.target.value)} required />
            <label htmlFor="login-password">密碼</label>
            <input id="login-password" name="password" type="password" autoComplete="current-password" placeholder="輸入密碼" value={password} onChange={(e) => setPassword(e.target.value)} required aria-describedby={error ? "login-error" : undefined} />
            {error && <p id="login-error" role="alert" className="form-error">{error}</p>}
            <button type="submit" className="primary-action login-submit" disabled={submitting || logoutIncomplete}>{submitting ? "登入中…" : "登入工作台"}</button>
            <div className="login-assistance"><button type="button" onClick={() => setHelp("請聯繫團隊管理員申請 StatsInsight 帳號。")}>申請帳號</button><button type="button" onClick={() => setHelp("請聯繫團隊管理員協助重設密碼。")}>忘記密碼？</button></div>
            {help && <div className="login-help" role="status">{help}<button type="button" onClick={() => setHelp("")}>關閉提示</button></div>}
          </form>
          <p className="login-footnote">專為棒球影片標記與記錄協作打造。</p>
        </section>
      </div>
    </main>
  );
};

export default Login;
